import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { AlertTriangle, Loader2, Send, Sparkles, X } from "lucide-react";
import { userReportsApi } from "../../api";
import type { GeneratedReportProposal, ReportChatResult } from "../../api/userReports";
import { useBrianProposal } from "../../hooks";
import type { UserReport } from "../../types/reports";
import { Button, DataTable } from "../ui";
import BrianPromptBar from "../Brian/BrianPromptBar";
import BrianProposalCard from "../Brian/BrianProposalCard";

interface ReportsBrianBuilderProps {
  onClose: () => void;
  onReview: (proposal: GeneratedReportProposal) => void;
  onCreated: (savedReport?: UserReport) => void;
}

export default function ReportsBrianBuilder({ onClose, onReview, onCreated }: ReportsBrianBuilderProps) {
  const [input, setInput] = useState("");
  const [clarification, setClarification] = useState("");
  const [creating, setCreating] = useState(false);
  const [makePublic, setMakePublic] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { response, loading, error, send, dismiss } = useBrianProposal<ReportChatResult>({
    request: async (message, conversation) =>
      (await userReportsApi.reportChat(message, conversation)).data,
    errorMessage: "Brian could not create that report",
  });

  function submitPrompt(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const message = input.trim();
    if (!message || loading) return;
    setInput("");
    void send(message);
  }

  function submitClarification(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const message = clarification.trim();
    if (!message || loading) return;
    setClarification("");
    void send(message);
  }

  function dismissResponse() {
    dismiss();
    setClarification("");
    inputRef.current?.focus();
  }

  async function createFromProposal(proposal: GeneratedReportProposal) {
    if (creating) return;
    setCreating(true);
    try {
      const saved = (
        await userReportsApi.create({
          name: proposal.name,
          description: proposal.description,
          sql_query: proposal.sql_query,
          is_public: makePublic,
        })
      ).data;
      onCreated(saved);
    } catch { return; } finally {
      setCreating(false);
    }
  }

  return (
    <section
      aria-label="Create a report with Brian"
      className="mb-5 rounded-xl border border-blue-100 bg-white p-4 shadow-sm"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Sparkles size={15} className="text-[#1D345F]" aria-hidden="true" />
            Create with Brian
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Describe the report you need. You can review the SQL and visibility before saving.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="Close Brian report builder"
        >
          <X size={16} />
        </button>
      </div>

      <BrianPromptBar
        inputRef={inputRef}
        autoFocus
        value={input}
        onChange={setInput}
        onSubmit={submitPrompt}
        loading={loading}
        placeholder="Ask Brian to create a report…"
      />

      <div className="mt-3" aria-live="polite">
        {error && (
          <MessageCard tone="error" message={error} onDismiss={dismissResponse} />
        )}

        {response?.type === "clarification" && (
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2.5">
                <Sparkles size={15} className="mt-0.5 shrink-0 text-[#1D345F]" aria-hidden="true" />
                <p className="text-sm text-gray-800">{response.message}</p>
              </div>
              <DismissButton onClick={dismissResponse} />
            </div>
            <form onSubmit={submitClarification} className="mt-3 flex gap-2">
              <input
                type="text"
                value={clarification}
                onChange={(event) => setClarification(event.target.value)}
                maxLength={2000}
                disabled={loading}
                placeholder="Your answer…"
                aria-label="Answer Brian's question"
                autoFocus
                className="min-w-0 flex-1 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm text-gray-800 placeholder-gray-400 focus:border-[#1D345F] focus:outline-none focus:ring-1 focus:ring-[#1D345F] disabled:opacity-60"
              />
              <Button type="submit" size="sm" disabled={loading || !clarification.trim()}>
                {loading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Send
              </Button>
            </form>
          </div>
        )}

        {response?.type === "not_found" && (
          <MessageCard tone="warning" message={response.message} onDismiss={dismissResponse} />
        )}

        {response?.type === "report_proposal" && response.report && (
          <BrianProposalCard
            message={response.message}
            onDismiss={dismissResponse}
            actions={
              <>
                <label className="mr-auto flex items-center gap-1.5 text-xs text-gray-600" title="Anyone can find this report in Discover">
                  <input type="checkbox" checked={makePublic} onChange={(event) => setMakePublic(event.target.checked)} />
                  Make public
                </label>
                <Button variant="secondary" size="sm" onClick={dismissResponse}>Dismiss</Button>
                <Button variant="secondary" size="sm" onClick={() => onReview(response.report!)}>Review &amp; edit</Button>
                <Button size="sm" onClick={() => createFromProposal(response.report!)} disabled={creating}>
                  {creating ? "Creating…" : "Create report"}
                </Button>
              </>
            }
          >
              <p className="text-sm font-semibold text-gray-900">{response.report.name}</p>
              {response.report.description && (
                <p className="mt-1 text-xs text-gray-500">{response.report.description}</p>
              )}

              <div className="mt-3">
                {response.preview?.error ? (
                  <p role="alert" className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
                    Preview error: {response.preview.error}
                  </p>
                ) : (
                  <>
                    <p className="mb-1.5 text-xs text-gray-400">
                      Preview · {response.preview?.row_count ?? 0} rows
                    </p>
                    <DataTable rows={response.preview?.rows} dense maxHeight="14rem" />
                  </>
                )}
              </div>
          </BrianProposalCard>
        )}
      </div>
    </section>
  );
}

function DismissButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600"
      aria-label="Dismiss Brian response"
    >
      <X size={14} />
    </button>
  );
}

function MessageCard({
  tone,
  message,
  onDismiss,
}: {
  tone: "warning" | "error";
  message: string;
  onDismiss: () => void;
}) {
  const classes = tone === "error"
    ? "border-red-100 bg-red-50 text-red-700"
    : "border-amber-100 bg-amber-50 text-gray-700";
  return (
    <div role={tone === "error" ? "alert" : undefined} className={`rounded-xl border p-4 ${classes}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
          <p className="text-sm">{message}</p>
        </div>
        <DismissButton onClick={onDismiss} />
      </div>
    </div>
  );
}
